import React, { useEffect, useState, useCallback } from "react";
import { useSelector } from "react-redux";
import "./PersonaManager.css";
import { PersonaCard } from "./PersonaCard";
import { PersonaExpanded, HrisEmployee } from "winslow-core";
import { apiGetPersonas } from "../../services/PersonasService";
import { selectUser } from "../../redux/features/appData";
import { apiGetAllEmployees } from "../../services/EmployeeService";

import { PersonaCreator } from "./PersonaCreator";
import { EmployeeCacheProvider } from "./EmployeeCacheProvider";
import { useEmployeeCache } from "../../hooks/useEmployeeCache";
import { Button } from "../../ds2/components";
import { PlusIcon } from "lucide-react";

// Component that loads employee data into the cache
const PersonaManagerWithData: React.FC = () => {
  const storeUser = useSelector(selectUser);
  const { setEmployees, setIsLoaded } = useEmployeeCache();

  useEffect(() => {
    const loadEmployeeData = async () => {
      if (storeUser?.currentWorkspaceId) {
        try {
          const employees = await apiGetAllEmployees(
            storeUser.currentWorkspaceId
          );
          if (employees) {
            setEmployees(employees);
            setIsLoaded(true);
          }
        } catch (error) {
          console.error("Failed to load employees for suggestions:", error);
        }
      }
    };

    loadEmployeeData();
  }, [storeUser?.currentWorkspaceId, setEmployees, setIsLoaded]);

  return <PersonaManagerContent />;
};

function PersonaManagerContent() {
  const storeUser = useSelector(selectUser);

  const [personas, setPersonas] = useState<PersonaExpanded[]>([]);

  const [isCreatorOpen, setIsCreatorOpen] = useState(false);

  const [selectedPersona, setSelectedPersona] =
    useState<PersonaExpanded | null>(null);

  const handlePersonaClick = (persona: PersonaExpanded) => {
    setSelectedPersona(persona);
    setIsCreatorOpen(true);
  };

  const refreshPersonas = useCallback(() => {
    if (storeUser && storeUser.currentWorkspaceId) {
      apiGetPersonas(storeUser.currentWorkspaceId).then((personaResults) => {
        if (personaResults) {
          setPersonas(
            personaResults.sort((a, b) => a.name.localeCompare(b.name))
          );
        }
      });
    }
  }, [storeUser]);

  const handleCreatorClose = () => {
    setIsCreatorOpen(false);
    setSelectedPersona(null);
  };

  useEffect(() => {
    refreshPersonas();
  }, [storeUser, refreshPersonas]);

  return (
    <div className="persona-manager-container">
      <div className="persona-manager-header">
        {/* <h1 className="persona-manager-title">Manage Personas</h1> */}
        <div className="page-title">Personas</div>

        <Button
          variant="primary"
          leftIcon={<PlusIcon size="16"/>}
          onClick={() => {
            setSelectedPersona(null);
            setIsCreatorOpen(true);
          }}>
          Persona
        </Button>

        {/* <button
          className="persona-manager-new-button"
          onClick={() => {
            setSelectedPersona(null);
            setIsCreatorOpen(true);
          }}>
          + New Persona
        </button> */}
      </div>
      <div className="persona-manager-cards-container">
        <div className="persona-manager-card-grid">
          {personas.map((persona) => (
            <div key={persona.id} className="persona-manager-card-column">
              <PersonaCard onClick={handlePersonaClick} persona={persona} />
            </div>
          ))}
        </div>
      </div>
      <PersonaCreator
        isOpen={isCreatorOpen}
        onClose={handleCreatorClose}
        existingPersona={selectedPersona}
        onPersonaSaved={refreshPersonas}
      />
    </div>
  );
}

// Main export that wraps everything with the cache provider
export default function PersonaManager() {
  return (
    <EmployeeCacheProvider>
      <PersonaManagerWithData />
    </EmployeeCacheProvider>
  );
}
